"use client";

import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Appliancerepairgrid() {
  const prefersReducedMotion = useReducedMotion();
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.52,
        delay: prefersReducedMotion ? 0 : i * 0.09,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
      }
    })
  };
  const photoCells = [{
    key: "refrigeration",
    label: "REFRIGERATION",
    sub: "Cooling \u00B7 Freezer \u00B7 Ice",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_fridge_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_fridge_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_fridge_service.png-wvc-sizes",
    alt: "Technician inspecting sealed system components on a stainless steel refrigerator",
    grid: "md:col-span-4 lg:col-span-4"
  }, {
    key: "laundry",
    label: "LAUNDRY",
    sub: "Washer \u00B7 Dryer \u00B7 Vent",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_laundry_unit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_laundry_unit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_laundry_unit.png-wvc-sizes",
    alt: "Service pro checking motor and drum assembly on a washing machine",
    grid: "md:col-span-6 lg:col-span-4"
  }, {
    key: "cooking",
    label: "COOKING APPLIANCES",
    sub: "Oven \u00B7 Range \u00B7 Stove",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_oven_range.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_oven_range.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_oven_range.png-wvc-sizes",
    alt: "Technician troubleshooting heating elements on a kitchen range oven",
    grid: "md:col-span-6 lg:col-span-5"
  }, {
    key: "dishwashers",
    label: "DISHWASHERS",
    sub: "Pump \u00B7 Seal \u00B7 Filter",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_dishwasher_pump.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_dishwasher_pump.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/repair_dishwasher_pump.png-wvc-sizes",
    alt: "Technician examining dishwasher circulation pump and drain assembly",
    grid: "md:col-span-6 lg:col-span-3"
  }];
  return <section data-section-id="32746" id="appliancerepairgrid" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.4
          }} variants={headerVariants} className="flex items-center gap-2">
              <span className="block h-[10px] w-[2px] bg-primary" aria-hidden="true" />
              <span className="font-default text-xs font-semibold uppercase text-primary" style={{
              letterSpacing: "0.22em"
            }}>
                Our Expertise
              </span>
              <span className="block h-[10px] w-[2px] bg-primary" aria-hidden="true" />
            </motion.div>

            <motion.h2 custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.4
          }} variants={headerVariants} className="font-default mt-5 uppercase text-foreground font-bold" style={{
            fontSize: "clamp(2.5rem, 5.6vw, 5rem)",
            lineHeight: 0.95,
            letterSpacing: "-0.005em"
          }}>
              Reliable Atlanta Repairs.
            </motion.h2>
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div data-index={0} className="md:col-span-12 lg:col-span-5 relative overflow-hidden rounded-md bg-card border border-border p-8 md:p-10 flex flex-col">
            <span className="font-default text-xs font-medium uppercase text-muted-foreground" style={{
            letterSpacing: "0.14em"
          }}>
              01 &middot; Services
            </span>
            <h3 className="mt-6 font-default text-foreground" style={{
            fontFamily: "var(--font-default)",
            fontWeight: 800,
            fontSize: "clamp(1.75rem, 2.6vw, 2.25rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.01em"
          }}>
              Expert Atlanta appliance care.
            </h3>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-md">
              Atlanta&apos;s humid summers and seasonal heat spikes put intense strain on household cooling and laundry equipment across Buckhead, Midtown, Old Fourth Ward, and Decatur. High heat causes compressor burnout, while humidity speeds up washer seal wear.
            </p>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-md">
              Local pros in our network troubleshoot mechanical breakdowns and provide repair solutions across the metro area. You can also explore our hub for <Link to="/ApplianceRepairPage" className="text-foreground font-medium underline hover:text-primary transition-colors">appliance repair in Atlanta</Link> and related <Link to="/HomeMaintenancePage" className="text-foreground font-medium underline hover:text-primary transition-colors">home maintenance</Link> services.
            </p>
            <div className="mt-8">
              <Button asChild className="rounded-lg uppercase font-semibold h-11 px-7" style={{
              letterSpacing: "0.14em"
            }}>
                <Link to="#bookingform">Find a local pro</Link>
              </Button>
            </div>

            <svg className="pointer-events-none absolute bottom-0 right-0 w-64 h-auto text-border" style={{
            opacity: 0.5
          }} viewBox="0 0 320 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M0 110 L40 70 L60 85 L95 45 L120 75 L155 30 L185 65 L215 40 L245 80 L275 55 L305 90 L320 78 L320 120 L0 120 Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <path d="M70 88 L74 78 L78 88 M74 78 L74 95" stroke="currentColor" strokeWidth="1.5" />
              <path d="M200 68 L204 58 L208 68 M204 58 L204 75" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </div>

          <PhotoCard data-index={1} item={photoCells[0]} className="md:col-span-8 lg:col-span-4" />

          <Link to="/ContactPage" data-index={2} className="md:col-span-4 lg:col-span-3 group relative overflow-hidden rounded-md bg-primary p-8 flex flex-col justify-between transition-colors duration-200 hover:bg-primary/90 min-h-[220px]">
            <h3 className="font-default uppercase text-primary-foreground font-bold" style={{
            fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
            lineHeight: 1.02,
            letterSpacing: "0.01em"
          }}>
              Request a quote.
            </h3>
            <div className="flex justify-end">
              <span className="inline-flex items-center justify-center rounded-lg border border-primary-foreground/30 p-3 transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1">
                <ArrowUpRight className="size-5 text-primary-foreground" strokeWidth={1.5} />
              </span>
            </div>
          </Link>

          <PhotoCard data-index={3} item={photoCells[1]} className={photoCells[1].grid} />

          <PhotoCard data-index={4} item={photoCells[2]} className={photoCells[2].grid} />

          <PhotoCard data-index={5} item={photoCells[3]} className={photoCells[3].grid} />
        </div>
      </div>
    </section>;
}
type PhotoItem = {
  key: string;
  label: string;
  sub: string;
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function PhotoCard({
  item,
  className,
  ...rest
}: {
  item: PhotoItem;
  className?: string;
  [key: string]: unknown;
}) {
  return <div {...rest} className={`group relative overflow-hidden rounded-md min-h-[260px] md:min-h-[300px] ${className ?? ""}`}>
      <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{
      background: "linear-gradient(180deg, transparent 45%, hsl(219 45% 12% / 0.75) 100%)"
    }} aria-hidden="true" />
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <div className="transition-transform duration-200 group-hover:-translate-y-0.5">
          <h3 className="font-default uppercase font-bold" style={{
          color: "hsl(36 30% 96%)",
          fontSize: "clamp(1.25rem, 1.75vw, 1.625rem)",
          lineHeight: 1.02,
          letterSpacing: "0.01em"
        }}>
            {item.label}
          </h3>
          <span className="mt-1 block h-px bg-primary origin-left transition-transform duration-300 ease-out scale-x-0 group-hover:scale-x-100" aria-hidden="true" />
          <p className="mt-3 font-default text-xs font-medium uppercase" style={{
          color: "hsl(36 30% 96%)",
          letterSpacing: "0.14em"
        }}>
            {item.sub}
          </p>
        </div>
      </div>
    </div>;
}